<script setup lang="ts">
import { reactive } from 'vue'
import { Icon } from '@iconify/vue'
import AccountPicker from '@/onenetwork/components/account_picker/AccountPicker.vue'
import RegionSelectComponent from '@/modules/broker/components/RegionSelectComponent.vue'

interface Props {
  show: boolean
}

interface Region {
  id: number
  name: string
  level1: { name: string }
}

interface ConditionClause {
  key: string
  op: string
  value: unknown
}

interface AccountLike {
  id?: number | string
  name?: string
  phone?: string
}

const props = defineProps<Props>()
const emit = defineEmits<{
  'on:close': []
  'on:confirm': [payload: { label: string; clauses: ConditionClause[] }]
}>()

// ---- Form state ----
const form = reactive({
  // AccountPicker emits an array of selected accounts (even for single select)
  exact_account: [] as AccountLike[],
  keyword: '',
  gender: '',
  age: '',
  register_date_from: '',
  register_date_to: '',
  birthday_day: '',
  birthday_month: '',
  kyc_status: '',
  account_types: [] as string[],
  broker_area_regions: [] as Region[],
  device_type: '',
  last_active: '',
})

// ---- Select options ----
const genderOptions = [
  { label: 'Nam', value: 'male' },
  { label: 'Nữ', value: 'female' },
  { label: 'Khác', value: 'other' },
]

const ageOptions = [
  { label: 'Tuổi > 20 AND tuổi <= 30', value: 'age_20_30' },
  { label: 'Tuổi > 30 AND tuổi <= 45', value: 'age_30_45' },
  { label: 'Tuổi > 45', value: 'age_45_plus' },
]

const kycOptions = [
  { label: 'Chưa KYC', value: 'not_kyc' },
  { label: 'Đang xử lý', value: 'pending' },
  { label: 'Đã KYC', value: 'verified' },
]

const deviceOptions = [
  { label: 'người dùng iOS', value: 'ios' },
  { label: 'người dùng Android', value: 'android' },
  { label: 'Tất cả thiết bị', value: 'all' },
]

const lastActiveOptions = [
  { label: '> 30 ngày', value: 'gt_30' },
  { label: '> 7 ngày', value: 'gt_7' },
  { label: 'Trong 7 ngày', value: 'lt_7' },
]

const dayOptions = Array.from({ length: 31 }, (_, i) => ({
  label: String(i + 1).padStart(2, '0'),
  value: String(i + 1).padStart(2, '0'),
}))

const monthOptions = Array.from({ length: 12 }, (_, i) => ({
  label: String(i + 1).padStart(2, '0'),
  value: String(i + 1).padStart(2, '0'),
}))

const accountTypeOptions = [
  { label: 'Tất cả loại tài khoản', value: 'all' },
  { label: 'Tất cả môi giới', value: 'broker' },
  { label: 'Khách', value: 'customer' },
  { label: 'Cấp trên trực tiếp', value: 'direct_superior' },
  { label: 'Giám đốc hệ thống', value: 'system_director' },
]

// ---- Region logic ----
function handleSelectRegion(region: Region) {
  if (region && !form.broker_area_regions.find((r) => r.id === region.id)) {
    form.broker_area_regions.push(region)
  }
}

function removeRegion(id: number) {
  form.broker_area_regions = form.broker_area_regions.filter((r) => r.id !== id)
}

// ---- Build label for condition tag ----
function buildLabel(): string {
  const parts: string[] = []

  if (form.exact_account.length) {
    const acc = form.exact_account[0]
    const name = acc?.name || acc?.phone || 'Tài khoản đã chọn'
    parts.push(`Chính xác TK: ${name}`)
  }
  if (form.keyword) parts.push(`Từ khóa: "${form.keyword}"`)
  if (form.gender) {
    const label = genderOptions.find((o) => o.value === form.gender)?.label
    parts.push(`Giới tính: ${label}`)
  }
  if (form.age) {
    const label = ageOptions.find((o) => o.value === form.age)?.label
    parts.push(`Tuổi: ${label}`)
  }
  if (form.register_date_from || form.register_date_to) {
    parts.push(`Ngày ĐK: ${form.register_date_from} → ${form.register_date_to}`)
  }
  if (form.birthday_day && form.birthday_month) {
    parts.push(`Ngày sinh: ${form.birthday_day}/${form.birthday_month}`)
  }
  if (form.kyc_status) {
    const label = kycOptions.find((o) => o.value === form.kyc_status)?.label
    parts.push(`KYC: ${label}`)
  }
  if (form.account_types.length) {
    const labels = form.account_types.map(
      (v) => accountTypeOptions.find((o) => o.value === v)?.label,
    )
    parts.push(`Tài khoản: ${labels.join(', ')}`)
  }
  if (form.broker_area_regions.length) {
    parts.push(`Khu vực: ${form.broker_area_regions.map((r) => r.name).join(', ')}`)
  }
  if (form.device_type) {
    const label = deviceOptions.find((o) => o.value === form.device_type)?.label
    parts.push(`Thiết bị: ${label}`)
  }
  if (form.last_active) {
    const label = lastActiveOptions.find((o) => o.value === form.last_active)?.label
    parts.push(`Hoạt động: ${label}`)
  }

  return parts.join(' | ') || 'Điều kiện mới'
}

function buildClauses(): ConditionClause[] {
  const clauses: ConditionClause[] = []

  // exact_account -> account_id in [ids]
  if (form.exact_account.length) {
    const ids = form.exact_account.map((a) => a?.id).filter((v) => v !== undefined && v !== null)
    if (ids.length) clauses.push({ key: 'account_id', op: 'in', value: ids })
  }

  // keyword -> name include
  if (form.keyword) {
    clauses.push({ key: 'name', op: 'include', value: form.keyword })
  }

  // gender -> eq
  if (form.gender) {
    clauses.push({ key: 'gender', op: 'eq', value: form.gender })
  }

  // age -> between (based on UI preset)
  if (form.age) {
    const map: Record<string, string> = {
      age_20_30: '20|30',
      age_30_45: '30|45',
      age_45_plus: '45|200',
    }
    const between = map[form.age]
    if (between) clauses.push({ key: 'age', op: 'between', value: between })
  }

  // regions -> in [ids]
  if (form.broker_area_regions.length) {
    clauses.push({ key: 'regions', op: 'in', value: form.broker_area_regions.map((r) => r.id) })
  }

  // register date range
  if (form.register_date_from && form.register_date_to) {
    clauses.push({ key: 'register_date', op: 'between', value: `${form.register_date_from}|${form.register_date_to}` })
  } else if (form.register_date_from) {
    clauses.push({ key: 'register_date', op: 'gte', value: form.register_date_from })
  } else if (form.register_date_to) {
    clauses.push({ key: 'register_date', op: 'lte', value: form.register_date_to })
  }

  // birthday
  if (form.birthday_day) clauses.push({ key: 'birthday_day', op: 'eq', value: form.birthday_day })
  if (form.birthday_month) clauses.push({ key: 'birthday_month', op: 'eq', value: form.birthday_month })

  // kyc
  if (form.kyc_status) clauses.push({ key: 'kyc_status', op: 'eq', value: form.kyc_status })

  // account types
  if (form.account_types.length) clauses.push({ key: 'account_type', op: 'in', value: [...form.account_types] })

  // device
  if (form.device_type) clauses.push({ key: 'device_type', op: 'eq', value: form.device_type })

  // last active
  if (form.last_active) clauses.push({ key: 'last_active', op: 'eq', value: form.last_active })

  return clauses
}

const handleConfirm = () => {
  const payload = { label: buildLabel(), clauses: buildClauses() }
  console.log('[push-campaign][ConditionModal] on:confirm', payload)
  emit('on:confirm', payload)
  resetForm()
}

const handleClose = () => {
  resetForm()
  emit('on:close')
}

function resetForm() {
  form.exact_account = []
  form.keyword = ''
  form.gender = ''
  form.age = ''
  form.register_date_from = ''
  form.register_date_to = ''
  form.birthday_day = ''
  form.birthday_month = ''
  form.kyc_status = ''
  form.account_types = []
  form.broker_area_regions = []
  form.device_type = ''
  form.last_active = ''
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="props.show"
      class="fixed inset-0 z-[100] flex items-center justify-center bg-black/40"
      @click.self="handleClose"
    >
      <div
        class="bg-white rounded-[16px] shadow-[0px_4px_16px_rgba(0,0,0,0.18)] w-[1433px] h-[710px] flex flex-col overflow-clip"
      >
        <!-- Header -->
        <div class="flex items-center justify-between px-8 py-5 border-b border-[#e2e2e2] shrink-0">
          <span class="font-bold text-[22px] text-black">Tùy chỉnh đối tượng</span>
          <button
            class="flex items-center justify-center w-7 h-7 rounded hover:bg-[#f0f0f0] cursor-pointer"
            @click="handleClose"
          >
            <Icon icon="material-symbols-light:close" width="22" />
          </button>
        </div>

        <!-- Body -->
        <div class="flex-1 overflow-y-auto px-8 py-6 flex flex-col justify-between gap-[12px]">
          <!-- Chính xác tài khoản (left half only) -->
          <div class="flex items-center gap-[10px]">
            <div class="flex items-center justify-end shrink-0 w-[247px]">
              <label class="text-[14px] font-bold text-black whitespace-nowrap"
                >Chính xác tài khoản</label
              >
            </div>
            <div class="flex-1 max-w-[360px]">
              <AccountPicker v-model="form.exact_account" placeholder="Chọn tài khoản" />
            </div>
          </div>

          <!-- Row: Từ khóa chứa | Giới tính -->
          <div class="flex gap-0">
            <!-- Left -->
            <div class="flex flex-1 items-center gap-[10px]">
              <div class="flex items-center justify-end shrink-0 w-[247px]">
                <label class="text-[14px] font-bold text-black whitespace-nowrap"
                  >Từ khóa chứa</label
                >
              </div>
              <div class="flex flex-1 items-center min-w-0">
                <input
                  v-model="form.keyword"
                  class="bg-[#f7f7f7] border-none rounded-[4px] px-4 py-[16px] text-[13px] text-[#333] outline-none placeholder:text-[#777] w-full"
                  placeholder="Từ khóa trong tên, sdt, mã code"
                />
              </div>
            </div>
            <!-- Right -->
            <div class="flex flex-1 items-center gap-[10px]">
              <div class="flex items-center justify-end shrink-0 w-[247px]">
                <label class="text-[14px] font-bold text-black whitespace-nowrap">Giới tính</label>
              </div>
              <div class="flex flex-1 items-center min-w-0">
                <Select
                  v-model="form.gender"
                  :options="genderOptions"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Chọn giới tính"
                  appendTo="body"
                  class="condition-select !p-0"
                />
              </div>
            </div>
          </div>

          <!-- Row: Tuổi | Ngày đăng ký -->
          <div class="flex gap-0">
            <!-- Left -->
            <div class="flex flex-1 items-center gap-[10px]">
              <div class="flex items-center justify-end shrink-0 w-[247px]">
                <label class="text-[14px] font-bold text-black whitespace-nowrap">Tuổi</label>
              </div>
              <div class="flex flex-1 items-center min-w-0">
                <Select
                  v-model="form.age"
                  :options="ageOptions"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Chọn độ tuổi"
                  appendTo="body"
                  class="w-full condition-select !p-0"
                />
              </div>
            </div>
            <!-- Right -->
            <div class="flex flex-1 items-center gap-[10px]">
              <div class="flex items-center justify-end shrink-0 w-[247px]">
                <label class="text-[14px] font-bold text-black whitespace-nowrap"
                  >Ngày đăng ký</label
                >
              </div>
              <div class="flex flex-1 items-center gap-2 min-w-0">
                <input
                  v-model="form.register_date_from"
                  type="date"
                  class="bg-[#f7f7f7] border-none rounded-[4px] px-4 py-[13px] text-[13px] text-[#333] outline-none flex-1 min-w-0"
                />
                <span class="text-[#777] shrink-0">→</span>
                <input
                  v-model="form.register_date_to"
                  type="date"
                  class="bg-[#f7f7f7] border-none rounded-[4px] px-4 py-[13px] text-[13px] text-[#333] outline-none flex-1 min-w-0"
                />
              </div>
            </div>
          </div>

          <!-- Row: Ngày sinh | Tình trạng KYC -->
          <div class="flex gap-0">
            <!-- Left -->
            <div class="flex flex-1 items-center gap-[10px]">
              <div class="flex items-center justify-end shrink-0 w-[247px]">
                <label class="text-[14px] font-bold text-black whitespace-nowrap">Ngày sinh</label>
              </div>
              <div class="flex items-center gap-[18px] shrink-0">
                <Select
                  v-model="form.birthday_day"
                  :options="dayOptions"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Ngày"
                  appendTo="body"
                  class="condition-select !p-0"
                />
                <span class="text-[16px] text-black shrink-0">/</span>
                <Select
                  v-model="form.birthday_month"
                  :options="monthOptions"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Tháng"
                  appendTo="body"
                  class="condition-select !p-0"
                />
              </div>
            </div>
            <!-- Right -->
            <div class="flex flex-1 items-center gap-[10px]">
              <div class="flex items-center justify-end shrink-0 w-[247px]">
                <label class="text-[14px] font-bold text-black whitespace-nowrap"
                  >Tình trạng KYC</label
                >
              </div>
              <div class="flex flex-1 items-center min-w-0">
                <Select
                  v-model="form.kyc_status"
                  :options="kycOptions"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Chọn tình trạng"
                  appendTo="body"
                  class="condition-select !p-0"
                />
              </div>
            </div>
          </div>

          <!-- Tài khoản (full width) -->
          <div class="flex items-start gap-[10px]">
            <div class="flex items-center justify-end shrink-0 w-[247px] pt-[2px]">
              <label class="text-[14px] font-bold text-black whitespace-nowrap">Tài khoản</label>
            </div>
            <div
              class="flex flex-wrap gap-x-[40px] gap-y-[10px]"
              style="
                display: grid;
                grid-template-columns: repeat(3, fit-content(100%));
                gap: 10px 40px;
              "
            >
              <label
                v-for="opt in accountTypeOptions"
                :key="opt.value"
                class="flex gap-[7px] items-center cursor-pointer"
              >
                <input
                  type="checkbox"
                  :value="opt.value"
                  v-model="form.account_types"
                  class="accent-[#0035ba] w-[16px] h-[16px] shrink-0"
                />
                <span class="text-[12px] text-black whitespace-nowrap">{{ opt.label }}</span>
              </label>
            </div>
          </div>

          <!-- Khu vực môi giới (full width) -->
          <div class="flex items-start gap-[10px]">
            <div class="flex items-center justify-end shrink-0 w-[247px] pt-[2px]">
              <label class="text-[14px] font-bold text-black whitespace-nowrap"
                >Khu vực môi giới</label
              >
            </div>
            <div class="flex flex-col gap-2 flex-1 min-w-0 max-w-[360px]">
              <RegionSelectComponent
                :regions="form.broker_area_regions"
                @selected="handleSelectRegion"
              />
              <hr v-if="form.broker_area_regions.length" class="border-[#e2e2e2]" />
              <div
                v-for="(region, idx) in form.broker_area_regions"
                :key="`region_${idx}`"
                class="flex items-center gap-1 text-[13px] text-[#333]"
              >
                {{ region.name }}, {{ region.level1?.name }}
                <span class="cursor-pointer text-[#e53935] ml-1" @click="removeRegion(region.id)">
                  <Icon icon="material-symbols:close" width="14" />
                </span>
              </div>
            </div>
          </div>

          <!-- Row: Loại thiết bị | Lần cuối hoạt động -->
          <div class="flex gap-0">
            <!-- Left -->
            <div class="flex flex-1 items-center gap-[10px]">
              <div class="flex items-center justify-end shrink-0 w-[247px]">
                <label class="text-[14px] font-bold text-black whitespace-nowrap"
                  >Loại thiết bị sử dụng</label
                >
              </div>
              <div class="flex flex-1 items-center min-w-0">
                <Select
                  v-model="form.device_type"
                  :options="deviceOptions"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Chọn thiết bị"
                  appendTo="body"
                  class="condition-select !p-0"
                />
              </div>
            </div>
            <!-- Right -->
            <div class="flex flex-1 items-center gap-[10px]">
              <div class="flex items-center justify-end shrink-0 w-[247px]">
                <label class="text-[14px] font-bold text-black whitespace-nowrap"
                  >Lần cuối hoạt động</label
                >
              </div>
              <div class="flex flex-1 items-center min-w-0">
                <Select
                  v-model="form.last_active"
                  :options="lastActiveOptions"
                  optionLabel="label"
                  optionValue="value"
                  placeholder="Chọn thời gian"
                  appendTo="body"
                  class="condition-select !p-0"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div
          class="flex items-center justify-end gap-3 px-8 py-5 border-t border-[#e2e2e2] shrink-0"
        >
          <button
            class="px-[22px] py-[14px] rounded-[4px] bg-[#272727] text-white text-[14px] font-bold hover:bg-[#444] cursor-pointer shadow-[0px_4px_4px_rgba(0,0,0,0.25)]"
            @click="handleConfirm"
          >
            Lưu đối tượng
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
:deep(.condition-select.p-select),
:deep(.condition-select .p-select-label) {
  background: #f7f7f7;
  border: none;
  font-size: 13px;
  padding: 13px 16px;
  color: #333;
}

:deep(.condition-select .p-select-label.p-placeholder) {
  color: #777;
}
</style>
